import type { AgendaItem, Meeting, MeetingType } from '@/modules/meetings/types';
import { carriesQuarterlySections } from '@/modules/meetings/quarterly-setup';

// Everything below the marker is mirrored verbatim into
// `supabase/functions/_shared/meeting-instance.ts`, which the materializer
// imports. This copy is canonical — regenerate with `bun run sync:shared`.

// ─── shared body ────────────────────────────────────────────────────────────

/**
 * The review and planning pair a cycle-bearing agenda is built against. All
 * four or nothing — `missingQuarterlyCycles` treats a partial set as absent.
 */
export interface QuarterlyCycles {
  reviewYear: number;
  reviewQuarter: number;
  planningYear: number;
  planningQuarter: number;
}

/** What a caller may say differently from the type's defaults. */
export interface MeetingInstanceOverrides {
  hostId?: string;
  /** `null` is Automatic — the roster follows the team, resolved at dispatch. */
  invitees?: string[] | null;
  agenda?: AgendaItem[];
  /** Set only to override the type; `undefined` leaves the instance inheriting. */
  prepEmailEnabled?: boolean | null;
  /** Set only to override the type; `undefined` leaves the instance inheriting. */
  notificationTimingHours?: number;
}

/**
 * The operational columns of a series, as overrides. `null` means "resolve
 * against the type", so the key is omitted rather than set to undefined — the
 * builder reads `!== undefined` to tell inheritance from an empty roster.
 *
 * Shared so the materializer and the divergence check cannot drift: one builds
 * the row, the other rebuilds it to ask whether anything changed.
 */
export interface SeriesOverrideSource {
  hostId?: string | null;
  invitees: string[] | null;
  prepEmailEnabled: boolean | null;
  notificationTimingHours: number | null;
}

/**
 * A manual roster after the host changes hands. The outgoing host is written in
 * explicitly: the merge below put them on every list without ever storing them,
 * so a rebuild would uninvite them. They leave when someone removes them.
 *
 * `null` is Automatic and stays Automatic — naming anyone ends the late binding.
 */
export function rosterAfterHostChange(
  roster: string[] | null,
  outgoingHost: string | null | undefined,
  incomingHost: string | null | undefined,
): string[] | null {
  if (roster === null) return null;
  return [...new Set([
    ...roster,
    ...(outgoingHost ? [outgoingHost] : []),
    ...(incomingHost ? [incomingHost] : []),
  ])];
}

export function seriesOverrides(series: SeriesOverrideSource): MeetingInstanceOverrides {
  return {
    ...(series.hostId ? { hostId: series.hostId } : {}),
    ...(series.invitees !== null ? { invitees: series.invitees } : {}),
    ...(series.prepEmailEnabled !== null ? { prepEmailEnabled: series.prepEmailEnabled } : {}),
    ...(series.notificationTimingHours !== null
      ? { notificationTimingHours: series.notificationTimingHours } : {}),
  };
}

/**
 * Everything the builder cannot derive. Each field is required, so a new call
 * site cannot compile without answering for it — that is the point, and the
 * reason this is one wide input rather than a short signature with defaults.
 */
export interface MeetingInstanceInput {
  id: string;
  /** The effective type, already resolved up the team chain. */
  type: MeetingType;
  /** UTC instant the meeting is scheduled for. */
  scheduledAt: string;
  /** The calendar date is read in the workspace's zone, never the browser's. */
  workspaceTz: string;
  /** The planning cycle to claim. Ignored unless the type claims cycles. */
  cycle: { year: number; quarter: number } | null;
  /** Resolved review/planning pair, or `null` when the caller cannot resolve both. */
  quarterlyCycles: QuarterlyCycles | null;
  /** Person id of whoever is creating it — the host fallback. */
  actingPersonId: string | null;
  /**
   * The series occurrence this materializes, or `null` for a one-off (#411 D3).
   *
   * `start` is the instant the rule produced. It stays put when the meeting is
   * moved, and the gap between it and `scheduledAt` is the whole record that
   * someone moved it on purpose.
   */
  occurrence: { scheduleId: string; start: string } | null;
  overrides?: MeetingInstanceOverrides;
}

/**
 * Whether instances of a type claim a planning cycle — get `year`/`quarter` and
 * seed quarterly setup (#409 D1).
 *
 * Derived from `builtIn`, which the resolver derives from the key rather than
 * from any stored row, so a team cannot acquire or lose a cycle claim by
 * overriding a type.
 */
export function claimsCycle(type: Pick<MeetingType, 'builtIn'>): boolean {
  return type.builtIn === true;
}

const agendaFromSections = (type: MeetingType): AgendaItem[] =>
  (type.sections ?? []).map((s) => ({ key: s.key, label: s.label, length: s.length, custom: s.custom }));

/**
 * The one constructor of a scheduled meeting (#409 D5).
 *
 * Three call sites used to build this shape inline and had drifted apart: the
 * series generator and the auto-next path both omitted the cycle, the host, and
 * quarterly setup, and both froze prep timing that the dispatcher resolves by
 * COALESCE. Every field is decided here now.
 *
 * Pure by construction: no store reads, no `crypto`, no clock. A caller
 * resolves the cycle, the acting person, and the quarterly pair and passes them
 * in. Reading stores from here is what would let the paths drift again.
 */
export function buildMeetingInstance(input: MeetingInstanceInput): Meeting {
  const { type, scheduledAt, workspaceTz, overrides = {} } = input;

  const agenda = overrides.agenda ?? agendaFromSections(type);
  const cadence = type.recurrence?.frequency;
  const claimed = claimsCycle(type) ? input.cycle : null;

  const hostId = overrides.hostId ?? type.defaultHostId ?? input.actingPersonId ?? undefined;

  const baseInvitees = overrides.invitees !== undefined ? overrides.invitees : type.defaultInvitees;
  const invitees = baseInvitees == null
    ? null
    : Array.from(new Set([...(hostId ? [hostId] : []), ...baseInvitees]));

  const seedsQuarterly = carriesQuarterlySections(type.sections) && input.quarterlyCycles != null;

  return {
    id: input.id,
    year: claimed?.year ?? undefined,
    quarter: claimed?.quarter ?? undefined,
    date: new Intl.DateTimeFormat('en-CA', { timeZone: workspaceTz }).format(new Date(scheduledAt)),
    scheduledAt,
    meetingScheduleId: input.occurrence?.scheduleId,
    occurrenceStart: input.occurrence?.start,
    meetingType: type.key,
    status: 'scheduled',
    sessionCount: 0,
    dataPeriodWeek: 1,
    reportMonthly: cadence === 'monthly',
    hostId,
    // `null` must survive as null: it means the roster follows the team and is
    // resolved at dispatch. Materialising it here freezes today's membership
    // into every future meeting.
    invitees: invitees as string[],
    agenda: agenda.length ? agenda : undefined,
    targetLengthMinutes: agenda.reduce((sum, item) => sum + item.length, 0) || 90,
    // Left unset unless the caller overrides, so the dispatcher's
    // COALESCE(meeting, type) keeps tracking the type.
    prepEmailEnabled: overrides.prepEmailEnabled,
    notificationTimingHours: overrides.notificationTimingHours,
    sectionData: seedsQuarterly ? { 'quarterly-setup': { ...input.quarterlyCycles } } : undefined,
    sectionTargets: {},
    resolvedIssueIds: [],
    createdTaskIds: [],
    attendees: [],
    fathomSummaries: [],
  };
}
